import { useMemo } from 'react';
import useSWR from 'swr';
import { useRouter } from 'next/router';
import fetchLiveWind from 'common/api/liveWind';
import closestBinarySearch from 'utils/closestBinarySearch';
import { convertToSeconds, oneDay, oneMinuteSeconds } from 'utils/time';
import { useAppSelector } from 'stores/hooks';
import midnightOfDate from 'utils/midnightOfDate';
import { getKbygQueryParams } from 'hooks/useCameraURLManagement';
import { getSpotReportAssociated } from '../../../selectors/spot';

// Live winds has one minute data resolution so we can cache for a minute
const ONE_MINUTE_IN_MS = 60 * 1000;

type FetchParams = {
  end: number;
  interval?: number;
  kbygQueryParams: Record<string, string | boolean>;
  spotId: string;
  start: number;
};

const useFetchLiveWind = (spotId: string, skip?: boolean) => {
  const router = useRouter();
  const kbygQueryParams = getKbygQueryParams(router?.query || {});

  const spotAssociated = useAppSelector(getSpotReportAssociated);

  const midnightToday = useMemo(
    () => midnightOfDate(spotAssociated?.utcOffset),
    [spotAssociated?.utcOffset],
  );

  const start = midnightToday;
  const end = midnightToday + convertToSeconds(oneDay);

  const args = useMemo<FetchParams | undefined>(
    () => (!skip ? { spotId, start, end, kbygQueryParams } : undefined),
    [end, kbygQueryParams, skip, spotId, start],
  );

  const {
    data,
    error,
    isLoading: loading,
  } = useSWR(
    args,
    ({
      end: endArg,
      interval: intervalArg,
      kbygQueryParams: kbygQueryParamsArg,
      spotId: spotIdArg,
      start: startArg,
    }: FetchParams) => fetchLiveWind(spotIdArg, startArg, endArg, intervalArg, kbygQueryParamsArg),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryCount: 0,
      dedupingInterval: ONE_MINUTE_IN_MS,
    },
  );

  const currentUnixTime = Math.floor(+new Date() / 1000);

  const currentWindIndex = data
    ? closestBinarySearch(data.data, { timestamp: currentUnixTime }, (item) => item.timestamp)
    : null;

  const currentWind = data && currentWindIndex !== null ? data.data[currentWindIndex] : null;

  // If sensorWind is false && the current data point is <5 minutes from the request time, look at the data point before that (previous point)
  // If previous point has sensorWind true, flip sensorWind to true on the current point. No need to change anything else.
  if (
    currentWind &&
    !currentWind.sensorWind &&
    !!currentWindIndex &&
    currentUnixTime - currentWind.timestamp < 5 * oneMinuteSeconds &&
    data?.data[currentWindIndex - 1].sensorWind
  ) {
    currentWind.sensorWind = true;
  }

  return {
    data,
    error,
    loading,
    currentWind,
  };
};

export default useFetchLiveWind;
